/**
 * FileFilterPanel Component
 * Advanced filter panel for file lists
 */

import { forwardRef } from 'react';
import { Stack, Group, MultiSelect, Button } from '@/components/ui';
import { Collapse } from '@/components/ui';
import { IconFilter, IconX } from '@tabler/icons-react';

export interface FileFilters {
  domains?: string[];
  fileTypes?: string[];
  status?: string[];
}

export interface FileFilterPanelProps {
  /** Current filter values */
  filters: FileFilters;
  /** Callback when filters change */
  onFilterChange: (filters: FileFilters) => void;
  /** Available domains */
  availableDomains?: string[];
  /** Available file types */
  availableTypes?: string[];
  /** Whether panel is open */
  opened?: boolean;
  /** Additional className */
  className?: string;
}

/**
 * Advanced filter panel for file lists
 *
 * @example
 * ```tsx
 * <FileFilterPanel
 *   filters={{ domains: ['User'], fileTypes: ['image'] }}
 *   onFilterChange={(filters) => applyFilters(filters)}
 *   availableDomains={['User', 'Member', 'Event']}
 *   availableTypes={['image', 'video', 'document']}
 *   opened
 * />
 * ```
 */
export const FileFilterPanel = forwardRef<HTMLDivElement, FileFilterPanelProps>(
  (props, ref) => {
    const {
      filters,
      onFilterChange,
      availableDomains = [],
      availableTypes = [],
      opened = false,
      className,
    } = props;

    const handleDomainChange = (value: string[] | null) => {
      onFilterChange({
        ...filters,
        domains: value || undefined,
      });
    };

    const handleTypeChange = (value: string[] | null) => {
      onFilterChange({
        ...filters,
        fileTypes: value || undefined,
      });
    };

    const handleStatusChange = (value: string[] | null) => {
      onFilterChange({
        ...filters,
        status: value || undefined,
      });
    };

    const handleClearFilters = () => {
      onFilterChange({});
    };

    const hasActiveFilters =
      (filters.domains && filters.domains.length > 0) ||
      (filters.fileTypes && filters.fileTypes.length > 0) ||
      (filters.status && filters.status.length > 0);

    return (
      <Collapse in={opened}>
        <Stack
          ref={ref}
          gap="md"
          p="md"
          style={{
            backgroundColor: 'var(--mantine-color-gray-0)',
            borderRadius: 'var(--mantine-radius-md)',
            border: '1px solid var(--mantine-color-gray-3)',
          }}
          className={className}
        >
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <IconFilter size={16} />
              <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Filters</span>
            </Group>

            {hasActiveFilters && (
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconX size={14} />}
                onClick={handleClearFilters}
              >
                Clear All
              </Button>
            )}
          </Group>

          <Group grow align="flex-start">
            {/* Domain Filter */}
            {availableDomains.length > 0 && (
              <MultiSelect
                label="Domain"
                placeholder="Select domains"
                data={availableDomains}
                value={filters.domains || []}
                onChange={handleDomainChange}
                searchable
                clearable
              />
            )}

            {/* File Type Filter */}
            {availableTypes.length > 0 && (
              <MultiSelect
                label="File Type"
                placeholder="Select file types"
                data={availableTypes}
                value={filters.fileTypes || []}
                onChange={handleTypeChange}
                searchable
                clearable
              />
            )}

            {/* Status Filter */}
            <MultiSelect
              label="Status"
              placeholder="Select status"
              data={[
                { value: 'uploaded', label: 'Uploaded' },
                { value: 'pending', label: 'Pending' },
                { value: 'deleted', label: 'Deleted' },
              ]}
              value={filters.status || []}
              onChange={handleStatusChange}
              clearable
            />
          </Group>
        </Stack>
      </Collapse>
    );
  }
);

FileFilterPanel.displayName = 'FileFilterPanel';

export default FileFilterPanel;
